import { PincodeRepository } from "../../domain/interfaces/PincodeRepository";
import { PincodeDetails } from "../../domain/models/PincodeDetails";
import { PINCODE_ENDPOINT } from "../../constant";
import { PincodeDetailsDTO } from "../dto/pincode.dto";
import { createApiSlice } from "@/shared/features/query/config/query.build";
import { ReduxQueryClient } from "@/shared/features/query/domain/interfaces/QueryClient";
import { adaptPincodeDetailsDtoToDomain } from "../mappers/pincode.mapper";
import { executeQuery } from "@/shared/features/query/utils/queryUtils";
import type { AppDispatch } from "@/app/store/store";
import { AxiosHttpClientAdapter } from "@/core/services/network/infra/adapters/axios.adapter";


const pincodeApiSlice = createApiSlice({
    clientId: 'pincodeApi',
    tagTypes: ['Pincode'],
    endpoints: {
        getPincodeDetails: {
            url: PINCODE_ENDPOINT,
            method: 'GET',
            withCredentials: true,
            params: (args: unknown) => {
                const { pincode = '' } = (args || {}) as { pincode?: string };
                return { pincode: String(pincode).trim() };
            },
            transformResponse: (response: unknown): PincodeDetails => {
                const dto = (typeof response === 'object' && response !== null ? response : {}) as PincodeDetailsDTO;
                return adaptPincodeDetailsDtoToDomain(dto);
            },
            providesTags: ['Pincode'],
        }
    },
});

export const pincodeApi = pincodeApiSlice.queryClient;
export const pincodeApiRedux = pincodeApiSlice.queryClient as ReduxQueryClient;

export class PincodeAdapter implements PincodeRepository {
    constructor(private readonly dispatch: AppDispatch) {}

    async fetchPincodeDetails(pincode: string): Promise<PincodeDetails | null> {
        const trimmed = pincode.trim();
        if (!trimmed) return null;

        try {
            const endpoint = pincodeApi.endpoints?.getPincodeDetails;
            if (endpoint && typeof endpoint.initiate === "function") {
                const initiateResult = endpoint.initiate({ pincode: trimmed });
                const result = await executeQuery<PincodeDetails>(initiateResult, this.dispatch);
                if (result && typeof result === "object") {
                    return result;
                }
            }
        } catch {
            // Fallback to direct request below
        }

        // Fallback path: dispatch request directly if RTK initiate path fails during runtime/HMR edge cases.
        // todo: need to remove this fallback path once we have a proper solution for the runtime/HMR edge cases.
        try {
            const httpClient = new AxiosHttpClientAdapter({
                headers: { Accept: "application/json, text/plain" },
            });
            const payload = await httpClient.get<unknown>(
                `${PINCODE_ENDPOINT}?pincode=${encodeURIComponent(trimmed)}`
            );
            if (!payload || typeof payload !== "object") return null;
            return adaptPincodeDetailsDtoToDomain(payload as PincodeDetailsDTO);
        } catch {
            return null;
        }
    }
}
